import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import type { SubmissionBrief, SubmissionView, Todo } from "@/app/reviewQueries";
import { Button } from "@/ui/Button";
import { StatusChip } from "@/ui/StatusChip";
import { cellWhere, commentsLabel, fmtWhen, reasonSummary, rootCount, runScore, STATUS_CHIP, STATUS_LABEL } from "@/routes/submission/model";
import { API_STATUS, canSend, CHECKLIST, NO_COMMENTS, SEND_LABEL, SEND_NOTE, VERDICT_LABEL, type Verdict } from "./model";

const Eyebrow = ({ children, fg }: { children: ReactNode; fg?: string }) => (
  <div className="font-mono text-[11px] uppercase tracking-[.06em]" style={{ color: fg ?? "var(--mut)" }}>
    {children}
  </div>
);

const Card = ({ children, testId, border }: { children: ReactNode; testId?: string; border?: string }) => (
  <section data-testid={testId} className="flex flex-col gap-3 rounded-md bg-card p-4" style={{ border: `1px solid ${border ?? "var(--bd)"}` }}>
    {children}
  </section>
);

const GRID: Record<string, { bg: string; fg: string; bd: string }> = {
  failed: { bg: "var(--errsoft)", fg: "var(--err)", bd: "var(--err)" },
  idle: { bg: "transparent", fg: "var(--mut)", bd: "var(--bd2)" },
  passed: { bg: "var(--oksoft)", fg: "var(--ok)", bd: "var(--ok)" },
};

/** «прогон студента»: счёт и клетки по задачам — из результата прогона. */
export function RunCard({ view }: { view: SubmissionView }) {
  const s = runScore(view);
  return (
    <Card testId="run-card">
      <div className="flex items-baseline justify-between">
        <Eyebrow>прогон студента</Eyebrow>
        <span className="font-mono text-[15px] font-bold" style={{ color: s && s.passed === s.total ? "var(--ok)" : "var(--err)" }}>
          {s ? `${s.passed} / ${s.total}` : "нет прогона"}
        </span>
      </div>
      {s ? (
        <div className="grid grid-cols-7 gap-[5px]">
          {s.cells.map((st, i) => (
            <span key={i} data-cell={st} className="grid aspect-square place-items-center rounded-[3px] font-mono text-[10px]" style={{ background: GRID[st]!.bg, color: GRID[st]!.fg, border: `1px solid ${GRID[st]!.bd}` }}>
              {i + 1}
            </span>
          ))}
        </div>
      ) : (
        <p className="m-0 text-[12.5px] leading-[1.5] text-ink2">Результат прогона недоступен — сдача без автопроверок, смотри процедуру вручную.</p>
      )}
    </Card>
  );
}

/** Чек-лист ревью: совет ментору, ничего не гейтит; сохраняется целиком. */
export function Checklist({ items, onChange }: { items: Record<string, boolean>; onChange: (items: Record<string, boolean>) => void }) {
  return (
    <Card testId="checklist">
      <Eyebrow>чек-лист ревью</Eyebrow>
      <div className="flex flex-col gap-2">
        {CHECKLIST.map((text, i) => {
          const id = String(i + 1);
          const on = !!items[id];
          return (
            <label key={id} className="flex cursor-pointer items-start gap-2.5 text-[12.5px] leading-[1.5]" style={{ color: on ? "var(--mut)" : "var(--ink2)" }}>
              <input type="checkbox" checked={on} onChange={() => onChange({ ...items, [id]: !on })} className="mt-[3px] size-[15px] accent-[var(--acc)]" />
              <span className="text-pretty">{text}</span>
            </label>
          );
        })}
      </div>
      <p className="m-0 text-[11.5px] leading-[1.5] text-mut">Совет, а не условие: вердикт от чек-листа не зависит, студент его не видит.</p>
    </Card>
  );
}

/** Сводка причин по комментариям: «пропуски и типы данных ×2». */
export function Reasons({ view }: { view: SubmissionView }) {
  const items = reasonSummary(view.threads);
  return (
    <Card testId="reasons">
      <Eyebrow>причины возврата</Eyebrow>
      {items.length === 0 ? (
        <p className="m-0 text-[12.5px] leading-[1.5] text-ink2">Причины появятся из комментариев к ячейкам — выбери в композере.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {items.map((r) => (
            <span key={r.label} className="rounded-[3px] border border-bd2 px-2 py-[3px] font-mono text-[11px] text-ink2">
              {r.label} ×{r.n}
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

function TodoList({ todos }: { todos: Todo[] }) {
  return (
    <ol className="m-0 flex list-none flex-col gap-2 p-0">
      {todos.map((t, i) => (
        <li key={`${t.cell_id}-${i}`} data-testid="todo" className="grid grid-cols-[18px_minmax(0,1fr)] gap-2">
          <span className="font-mono text-[11px]" style={{ color: "var(--err)" }}>
            {i + 1}
          </span>
          <span className="min-w-0">
            <span className="block text-[12.5px] leading-[1.5] text-ink text-pretty">{t.text}</span>
            <span className="block font-mono text-[10.5px] text-ink2" title={t.cell_id}>
              {cellWhere(t)}
            </span>
          </span>
        </li>
      ))}
    </ol>
  );
}

/** Вердикт на проверке: сегменты, сводка, перечень из комментариев, кнопка с
 *  правилом §2.1 — «вернуть» без комментариев заблокирована с причиной. */
export function VerdictCard({ view, onSend, busy, error }: { view: SubmissionView; onSend: (status: "accepted" | "changes_requested", summary: string) => Promise<void>; busy: boolean; error: string | null }) {
  const [verdict, setVerdict] = useState<Verdict>("revise");
  const [summary, setSummary] = useState("");
  const roots = rootCount(view);
  const ok = canSend(verdict, roots);
  return (
    <Card testId="verdict-card" border="var(--bd2)">
      <div className="-m-4 mb-0 flex items-center justify-between rounded-t-md bg-bg2 px-4 py-2.5">
        <Eyebrow>вердикт</Eyebrow>
        <span className="font-mono text-[11px] text-ink2" data-testid="comment-count">
          {commentsLabel(roots)}
        </span>
      </div>
      <div className="flex gap-[3px] rounded-sm border border-bd p-[3px]" aria-label="вердикт">
        {(["accept", "revise"] as Verdict[]).map((v) => {
          const on = verdict === v;
          const tone = v === "accept" ? { bg: "var(--oksoft)", fg: "var(--ok)" } : { bg: "var(--errsoft)", fg: "var(--err)" };
          return (
            <button key={v} type="button" aria-pressed={on} onClick={() => setVerdict(v)} className="flex-1 cursor-pointer rounded-[3px] border-0 py-[7px] font-mono text-[12px]" style={{ background: on ? tone.bg : "transparent", color: on ? tone.fg : "var(--mut)" }}>
              {VERDICT_LABEL[v]}
            </button>
          );
        })}
      </div>
      <textarea value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="одно-два предложения студенту" rows={4} className="w-full resize-y rounded-sm border border-bd2 bg-bg2 px-[11px] py-2 text-[13px] leading-[1.5] text-ink outline-none focus:border-acc" />
      {view.todos.length > 0 ? (
        <TodoList todos={view.todos} />
      ) : (
        <p className="m-0 text-[12.5px] leading-[1.5] text-ink2" data-testid={ok ? "no-todo" : "verdict-reason"}>
          {NO_COMMENTS}
        </p>
      )}
      <Button variant={verdict === "accept" ? "primary" : "danger"} className="w-full justify-center py-[11px]" disabled={!ok || busy} onClick={() => void onSend(API_STATUS[verdict], summary.trim())} data-testid="send-verdict">
        {busy ? "отправляем…" : SEND_LABEL[verdict]}
      </Button>
      {error && (
        <p role="alert" data-testid="verdict-error" className="m-0 text-[12px] leading-[1.5]" style={{ color: "var(--err)" }}>
          {error}
        </p>
      )}
      <p className="m-0 text-[12px] leading-[1.5] text-mut text-pretty">{SEND_NOTE[verdict]}</p>
    </Card>
  );
}

/** После вердикта: состояние, сводка, перечень правок, «следующая в очереди». */
export function VerdictDone({ view }: { view: SubmissionView }) {
  const accepted = view.status === "accepted";
  const fg = accepted ? "var(--ok)" : "var(--err)";
  return (
    <Card testId="verdict-done" border={fg}>
      <div className="-m-4 mb-0 flex items-center justify-between rounded-t-md px-4 py-2.5" style={{ background: accepted ? "var(--oksoft)" : "var(--errsoft)" }}>
        <Eyebrow fg={fg}>вердикт</Eyebrow>
        <StatusChip status={STATUS_CHIP[view.status]} compact />
      </div>
      <div className="font-mono text-[11px] text-ink2">
        {view.reviewer?.name ?? "ментор"}
        {view.verdict_at ? ` · ${fmtWhen(view.verdict_at)}` : ""}
      </div>
      {view.summary && <p className="m-0 text-[13px] leading-[1.55] text-ink text-pretty">{view.summary}</p>}
      {accepted ? (
        <p className="m-0 text-[12.5px] leading-[1.5] text-ink2">Гейт шага пересчитан — критерий «домашка принята» выполнен, студент видит вердикт и сводку.</p>
      ) : view.todos.length > 0 ? (
        <TodoList todos={view.todos} />
      ) : (
        <p className="m-0 text-[12.5px] leading-[1.5] text-ink2">Перечень правок пуст.</p>
      )}
      <Link to="/mentor/queue" className="font-mono text-[12px] text-acc underline-offset-2 hover:underline">
        следующая в очереди →
      </Link>
    </Card>
  );
}

/** «история студента»: предыдущие итерации по этому заданию. */
export function HistoryCard({ history }: { history: SubmissionBrief[] }) {
  return (
    <Card testId="history-card">
      <Eyebrow>история студента</Eyebrow>
      {history.length === 0 ? (
        <p className="m-0 text-[12.5px] leading-[1.5] text-ink2">Первая сдача по этому заданию.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {[...history]
            .sort((a, b) => b.iteration - a.iteration)
            .map((h) => (
              <div key={h.id} data-testid="history-row" className="grid grid-cols-[82px_minmax(0,1fr)] items-baseline gap-2.5">
                <span className="font-mono text-[11px] text-ink2">{fmtWhen(h.submitted_at)}</span>
                <span className="text-[12.5px] leading-[1.5]">
                  <Link to="/mentor/review/$submissionId" params={{ submissionId: h.id }} className="font-mono text-ink underline-offset-2 hover:underline">
                    v{h.iteration}
                  </Link>
                  <span className="text-ink2"> · {STATUS_LABEL[h.status]}</span>
                </span>
              </div>
            ))}
        </div>
      )}
    </Card>
  );
}
